// src/use-places-autocomplete.ts
import { onMounted, reactive, toRefs, watch } from "vue";
import { Loader } from "@googlemaps/js-api-loader";
import debounceFn from "debounce-fn";

// src/helpers/autocompletionRequestBuilder.ts
var autocompletionRequestBuilder_default = (autocompletionRequest, input, sessionToken) => {
  const { bounds, location, ...rest } = autocompletionRequest;
  const res = {
    input,
    ...rest
  };
  if (sessionToken)
    res.sessionToken = sessionToken;
  if (bounds)
    res.bounds = new google.maps.LatLngBounds(...bounds);
  if (location)
    res.location = new google.maps.LatLng(location);
  return res;
};

// src/use-places-autocomplete.ts
function usePlacesAutocomplete(query, {
  apiKey = "",
  apiOptions = {},
  autocompletionRequest = {},
  debounce = 300,
  minLengthAutocomplete = 0,
  onLoadFailed = console.error,
  withSessionToken
} = {}) {
  const state = reactive({
    placesService: void 0,
    sessionToken: void 0,
    suggestions: [],
    loading: false
  });
  const fetchSuggestions = () => {
    if (!state.placesService) {
      state.suggestions = [];
      return;
    }
    if (query.value.length < minLengthAutocomplete || !query.value.length) {
      state.suggestions = [];
      return;
    }
    const autocompletionReq = { ...autocompletionRequest };
    state.loading = true;
    state.placesService?.getPlacePredictions(
      autocompletionRequestBuilder_default(
        autocompletionReq,
        query.value,
        withSessionToken && state.sessionToken
      ),
      (result) => {
        state.suggestions = result || [];
        state.loading = false;
      }
    );
  };
  watch(query, debounceFn(fetchSuggestions, { wait: debounce }));
  const refreshSessionToken = () => {
    state.sessionToken = new google.maps.places.AutocompleteSessionToken();
  };
  const initializeService = () => {
    if (!window.google) {
      console.error("[vue-use-places-autocomplete]: Google script not loaded");
      return;
    }
    if (!window.google.maps) {
      console.error("[vue-use-places-autocomplete]: Google maps script not loaded");
      return;
    }
    if (!window.google.maps.places) {
      console.error("[vue-use-places-autocomplete]: Google maps places script not loaded");
      return;
    }
    state.placesService = new window.google.maps.places.AutocompleteService();
    refreshSessionToken();
    fetchSuggestions();
  };
  onMounted(() => {
    const init = async () => {
      try {
        if (!window.google || !window.google.maps || !window.google.maps.places)
          await new Loader({ apiKey, ...{ libraries: ["places"], ...apiOptions } }).load();
        initializeService();
      } catch (error) {
        onLoadFailed(error);
      }
    };
    if (apiKey)
      init();
    else
      initializeService();
  });
  return {
    ...toRefs(state),
    refreshSessionToken
  };
}

// src/utils/getLatLng.ts
var getLatLng = (result) => new Promise((resolve, reject) => {
  try {
    const latLng = {
      lat: result.geometry.location.lat(),
      lng: result.geometry.location.lng()
    };
    return resolve(latLng);
  } catch (e) {
    return reject(e);
  }
});
var getLatLng_default = getLatLng;

// src/utils/getGeocode.ts
var getGeocode = (args) => {
  const geocoder = new window.google.maps.Geocoder();
  const { OK } = window.google.maps.GeocoderStatus;
  return new Promise((resolve, reject) => {
    geocoder.geocode(
      args,
      (results, status) => {
        if (status !== OK)
          return reject(status);
        return resolve(results || []);
      }
    );
  });
};
var getGeocode_default = getGeocode;
export {
  getGeocode_default as getGeocode,
  getLatLng_default as getLatLng,
  usePlacesAutocomplete
};
